import { memo, useState, useEffect } from "react";
import {
  Group,
  Flex,
  Text,
  Button,
  List,
  useMantineTheme,
} from "@mantine/core";
import { useCatalogContext } from "src/api/context/CatalogContext";
import TextInput from "src/components/TextInput/TextInput";
import { useDiscountByCodeLazyQuery } from "src/generated/graphql";
import { useAppContext } from "src/api/AppContext";
import { formatter } from "src/utils/constants";
import { errorNotification } from "src/utils/alerts";

const borderBottom = "1px solid #DDDDDD";

function Discount() {
  const theme = useMantineTheme();
  const { setappliedCodes, appliedCodes, productsCart } = useCatalogContext();
  const { currentLocation } = useAppContext();
  const [discountCode, setdiscountCode] = useState("");

  const [checkDiscount, { loading }] = useDiscountByCodeLazyQuery({
    onCompleted: (data) => {
      setdiscountCode("");
      // check if not already added
      if (appliedCodes.find((d) => d.id))
        return errorNotification({ message: "Discount Code already added!" });

      // Check if productsCart has product for which discountCode applied
      const productIsInDiscount = productsCart.some((cartItem) =>
        data.discountByCode.products.some(
          (product) => product.id === cartItem.id
        )
      );
      if (!productIsInDiscount)
        return errorNotification({
          message: "The supplied code is not available on these products!",
        });
      setappliedCodes([...appliedCodes, data.discountByCode]);
    },
  });

  useEffect(() => {
    // Filter appliedDiscounts based on matching IDs with ProductCart
    const matchingDiscounts = appliedCodes.filter((discount) =>
      discount.products.some((product) =>
        productsCart.some((cartItem) => cartItem.id === product.id)
      )
    );
    if (matchingDiscounts.length !== appliedCodes.length) {
      setappliedCodes(matchingDiscounts);
    }
  }, []);

  const handleAddCode = () => {
    if (!currentLocation) return;
    checkDiscount({
      variables: { code: discountCode, locationId: currentLocation.id },
    });
  };

  return (
    <>
      <Flex justify={"space-between"} sx={{ borderBottom }} pb={6}>
        <Text size={18} fw={500}>
          Discounts
        </Text>
        <Group>
          <TextInput
            mt={0}
            size="sm"
            placeholder="Enter Discount Code"
            value={discountCode}
            maxLength={20}
            onChange={(e) => setdiscountCode(e.target.value)}
          />
          <Button
            size="sm"
            sx={{
              height: 34,
              backgroundColor: !discountCode
                ? `${theme.colors.secondary[6]} !important`
                : undefined,
              color: !discountCode
                ? `${theme.colors.secondary[1]} !important`
                : undefined,
            }}
            onClick={handleAddCode}
            disabled={!discountCode}
            loading={loading}
          >
            Add Code
          </Button>
        </Group>
      </Flex>

      <List
        styles={{
          itemWrapper: {
            width: "100%",
          },
          item: {
            listStyle: "none",
            borderBottom,
            padding: "4px 0",
            margin: " 2px 10px",
            color: theme.colors.secondaryTextColor[4],
          },
        }}
      >
        {appliedCodes.length === 0 && (
          <Text
            fw={600}
            size={18}
            pt="xs"
            align="center"
            w={"100%"}
            c={theme.colors.secondaryTextColor[4]}
          >
            No Discounts Added
          </Text>
        )}
        {appliedCodes?.map((discount) => {
          return (
            <List.Item key={discount.id}>
              <Flex align={"center"} gap={10}>
                <Text size={14} fw={600}>
                  {discount.name}
                </Text>
                <Text size={14} fw={500}>
                  {discount.isPercent
                    ? `${discount.value}% `
                    : formatter.format(discount.value)}
                  Off
                </Text>
              </Flex>
            </List.Item>
          );
        })}
      </List>
    </>
  );
}

export default memo(Discount);
